import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@crashx/ui";
import type { ChatMessage } from "@crashx/shared";
import { chatApi, chatError } from "./chatApi.js";
import { useChatStore } from "./chatStore.js";

export function ChatModeration({ message }: { message: ChatMessage }) {
  const { t } = useTranslation();
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState(300000);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function apply(action: "mute" | "ban" | "delete") {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await chatApi.moderate({ messageId: message.id, action, durationMs: duration });
      if (action === "delete") useChatStore.getState().remove(message.id);
      setOpen(false);
      trigger.current?.focus();
    } catch (cause) {
      setError(chatError(cause));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
      }}
    >
      <Button
        ref={trigger}
        variant="ghost"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
        aria-label={t("chat.moderate", { nick: message.nick })}
      >
        {t("chat.actions")}
      </Button>
      {open ? (
        <div id={id} className="flex flex-col gap-2">
          <label className="cx-field">
            {t("chat.duration")}
            <select
              className="cx-input"
              value={duration}
              onChange={(event) => setDuration(Number(event.target.value))}
            >
              {[1, 5, 60].map((minutes) => (
                <option key={minutes} value={minutes * 60000}>
                  {t("chat.minutes", { count: minutes })}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            {(["mute", "ban", "delete"] as const).map((action) => (
              <Button key={action} variant="ghost" disabled={busy} onClick={() => void apply(action)}>
                {t(`chat.${action}`)}
              </Button>
            ))}
          </div>
          {error ? (
            <p role="alert" className="text-danger">
              {t(error)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
